/**
 * The company logo: one file, at one canonical path, in a private bucket.
 *
 * Everything about the path is server-enforced, not merely client-behaved:
 * `workspace_logos_insert/update/delete` (0010) check `settings.manage`
 * against the path's own first segment via `storage_workspace_id()`, so a
 * client that somehow requested a different workspace's path would be
 * refused by the same policy that protects every other tenant table. This
 * file's job is narrower — validate before spending an upload, and never
 * trust the browser's own filename for anything.
 */
import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabaseClient';

const BUCKET = 'workspace-logos';
const MAX_BYTES = 524_288; // matches the bucket's own file_size_limit (0010)
const ALLOWED_MIME: Record<string, string> = {
    'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp',
};
/** Sane bounds for a logo, not a photograph: neither a favicon nor a poster. */
const MIN_DIMENSION = 32;
const MAX_DIMENSION = 4000;

export class LogoError extends Error {}

async function readImageDimensions(file: File): Promise<{ width: number; height: number }> {
    const bitmap = await createImageBitmap(file).catch(() => null);
    if (!bitmap) throw new LogoError('That file does not look like a real image.');
    const size = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return size;
}

/**
 * Every check a file must pass before it is worth an upload. Nothing here is
 * the authoritative boundary — the bucket's own `allowed_mime_types` and
 * `file_size_limit` (0010) are — this exists so a person sees why their file
 * was refused instead of a storage error code.
 */
export async function validateLogoFile(file: File): Promise<string> {
    const ext = ALLOWED_MIME[file.type];
    if (!ext) throw new LogoError('Use a PNG, JPEG or WebP image. SVG is not accepted.');
    if (file.size > MAX_BYTES) throw new LogoError('That image is larger than 512 KB. Use a smaller one.');
    if (file.size === 0) throw new LogoError('That file is empty.');

    const { width, height } = await readImageDimensions(file);
    if (width < MIN_DIMENSION || height < MIN_DIMENSION) {
        throw new LogoError(`That image is too small (${width}×${height}px). Use at least ${MIN_DIMENSION}×${MIN_DIMENSION}px.`);
    }
    if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        throw new LogoError(`That image is too large (${width}×${height}px). Use at most ${MAX_DIMENSION}×${MAX_DIMENSION}px.`);
    }
    return ext;
}

/**
 * The canonical, generated path — never the file the browser handed in.
 * One logo per company: uploading again replaces it at the same path (the
 * extension can change between uploads; the previous object is removed
 * first so a switch from .png to .webp cannot leave an orphaned file behind
 * that nothing ever points to again but nothing ever deletes either).
 */
const pathFor = (workspaceId: string, ext: string): string => `${workspaceId}/logo.${ext}`;

export async function uploadLogo(workspaceId: string, currentPath: string | null, file: File): Promise<string> {
    const ext = await validateLogoFile(file);
    const path = pathFor(workspaceId, ext);
    const storage = supabase().storage.from(BUCKET);

    if (currentPath && currentPath !== path) {
        await storage.remove([currentPath]).catch(() => {});
    }

    const { error: uploadError } = await storage.upload(path, file, { contentType: file.type, upsert: true });
    if (uploadError) {
        throw new LogoError(
            /row-level security|policy|permission/i.test(uploadError.message)
                ? 'You do not have permission to change the company logo.'
                : 'We could not upload that image. Try again in a moment.',
        );
    }

    const { error: updateError } = await supabase()
        .from('workspaces').update({ logo_path: path }).eq('id', workspaceId);
    if (updateError) throw new LogoError('The image uploaded, but we could not save it against your company. Try again.');

    return path;
}

export async function removeLogo(workspaceId: string, currentPath: string): Promise<void> {
    await supabase().storage.from(BUCKET).remove([currentPath]);
    const { error } = await supabase().from('workspaces').update({ logo_path: null }).eq('id', workspaceId);
    if (error) throw new LogoError('We could not remove the logo. Try again in a moment.');
}

/**
 * A signed URL for a private object — the bucket is not public (0010), so a
 * bare path is never renderable on its own. Cached in module scope per path
 * for the life of the tab; a replaced logo gets a new path, so this never
 * serves a stale image for a changed one.
 */
const urlCache = new Map<string, { url: string; expiresAt: number }>();

export function useLogoUrl(logoPath: string | null): string | null {
    const [url, setUrl] = useState<string | null>(null);
    const issuedFor = useRef(logoPath);

    useEffect(() => {
        issuedFor.current = logoPath;
        if (!logoPath) { setUrl(null); return; }

        const cached = urlCache.get(logoPath);
        if (cached && cached.expiresAt > Date.now()) { setUrl(cached.url); return; }

        setUrl(null);
        void supabase().storage.from(BUCKET).createSignedUrl(logoPath, 3600).then(({ data }) => {
            if (issuedFor.current !== logoPath || !data) return;
            urlCache.set(logoPath, { url: data.signedUrl, expiresAt: Date.now() + 3600 * 1000 - 30_000 });
            setUrl(data.signedUrl);
        });
    }, [logoPath]);

    return url;
}
